import { useEffect, useState } from "react";
import classNames from "classnames";
import { useMutation } from "react-query";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { StandardLayout } from "@/react/components/Layout/StandardLayout";
import { StandardLayoutHeaderWithPortal } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeader/StandardLayoutHeaderWithPortal";
import { Bookmarks } from "@/react/components/Spaces/Bookmarks";
import { SpaceHeaderActions } from "@/react/components/Spaces/Header/SpaceHeaderActions";
import { SpaceHeaderTitle } from "@/react/components/Spaces/Header/SpaceHeaderTitle";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import {
  getSpaceNotificationDetail,
  isMembersSpace,
  shouldRedirectToSpaceMembersPage,
  spaceMetaTitle,
} from "@/react/helpers/spaceHelpers";
import { internalApi, spaces } from "@/react/helpers/urlHelpers";
import { useAppendBodyClasses } from "@/react/hooks/useAppendBodyClasses";
import { useDynamicLayoutContext } from "@circle-react/components/Layout/DynamicLayout";
import { SpaceCoverForm } from "@circle-react/components/Layout/StandardLayoutV2/SpaceCoverForm";
import { VersionedContent } from "@circle-react/components/Layout/VersionedContent";
import { useIsLockedSpaceWithBlocks } from "@circle-react/components/Spaces/hooks";
import {
  useCurrentSpaceContext,
  useSpaceResource,
  useSpacesContext,
} from "@circle-react/contexts";
import { useSpaceNotificationsState } from "@circle-react/contexts/sidebarNotificationsContext";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import { useIsLockedSpace } from "@circle-react/hooks/useIsLockedSpace";
import { useSpaceSidebarNotifications } from "@circle-react/hooks/useSpaceSidebarNotifications";
import { RightSidebar } from "../Show/RightSidebar";
import { SpaceFeedWrapper } from "../Show/SpaceFeedWrapper";
import { SpaceMembers } from "../Show/SpaceMembers";
import { VersionedSpaceWrapper } from "../Show/VersionedSpaceWrapper";
import { Content } from "./Content";

export const EventsSpace = () => {
  const isLockedSpaceWithBlocks = useIsLockedSpaceWithBlocks();
  const { data } = useCurrentSpaceContext();
  const space = data;
  const spacesContext = useSpacesContext();
  const { refetchSpaceNotifications } = useSpaceSidebarNotifications([
    space?.id,
  ]);

  const [activeViewType, setActiveViewType] = useState(space?.display_view);

  const { refetch: refetchSpace } = useSpaceResource({
    spaceId: space?.id,
  });

  const { spaces: spaceNotifications } = useSpaceNotificationsState();
  const spaceNotificationDetail = getSpaceNotificationDetail(
    spaceNotifications,
    space?.id,
  );

  const bodyClassNames = classNames(
    `view-space view-space--${space?.id} view-space-in-group view-space-in-group--${space?.space_group_id}`,
  );

  useAppendBodyClasses(bodyClassNames);
  const isLockedSpace = useIsLockedSpace();

  const isMembersTabActive = useRouteMatch("/c/:spaceSlug/members");
  const history = useHistory();
  const isIframeEmbed = useIsIframeEmbed();
  const { setEmptyLayout, setStandardLayout } = useDynamicLayoutContext();

  const shouldShowBookmarks =
    !isLockedSpace &&
    !isMembersTabActive &&
    !isIframeEmbed &&
    !isMembersSpace(space);
  const shouldShowRightSidebar = !isLockedSpace && !isIframeEmbed;

  useEffect(() => {
    if (!isIframeEmbed) return;
    setEmptyLayout();
    return setStandardLayout;
  }, [isIframeEmbed, setEmptyLayout, setStandardLayout]);

  useEffect(() => {
    if (!spacesContext.isLoading && space?.id) {
      if (spaceNotificationDetail?.unread_content_count > 0) {
        void resetUnreadCount();
      }
      setActiveViewType(space?.display_view);
    }
    if (
      !isMembersTabActive &&
      shouldRedirectToSpaceMembersPage(space) &&
      space?.slug
    ) {
      history.push(spaces.members({ spaceSlug: space.slug }));
    }
  }, [spacesContext.isLoading, space, spaceNotificationDetail]);

  const { mutateAsync: resetUnreadCount } = useMutation(
    () =>
      reactQueryPost(
        internalApi.spaces.resetUnreadCount({ spaceId: space?.id }),
        {
          forReactQuery: true,
        },
      ),
    {
      onSuccess: updatedSpace => {
        spacesContext.actions.updateSpace(updatedSpace);
        void refetchSpaceNotifications();
      },
      onError: err => {
        console.error("Error while resetting the unread count", err);
      },
    },
  );

  if (!space) {
    return null;
  }

  return (
    <VersionedContent fullWidth fullHeight={false} noPadding>
      <StandardLayout.Breadcrumb />
      <StandardLayoutHeaderWithPortal
        leftContent={<SpaceHeaderTitle space={space} hasDropdown={false} />}
        rightContent={
          !isIframeEmbed && (
            <SpaceHeaderActions
              space={space}
              onViewTypeChange={setActiveViewType}
              activeViewType={activeViewType}
            />
          )
        }
      >
        {shouldShowBookmarks && <Bookmarks />}
      </StandardLayoutHeaderWithPortal>
      <StandardLayout.Body
        hasPaddingTopMobile={false}
        wrapperClassName={classNames({
          "!p-0 !m-0": isLockedSpace,
        })}
      >
        <PageMetaData title={spaceMetaTitle(space)} />
        <VersionedSpaceWrapper isLockedSpace={isLockedSpace}>
          <SpaceFeedWrapper>
            {!isLockedSpaceWithBlocks && <SpaceCoverForm space={space} />}
            <Switch>
              <Route
                path="/c/:spaceSlug/members"
                render={() => <SpaceMembers space={space} />}
              />
              <Route
                path="/c/:spaceSlug"
                render={() => (
                  <Content
                    space={space}
                    isLockedSpace={isLockedSpace}
                    refetchSpace={refetchSpace}
                  />
                )}
              />
            </Switch>
          </SpaceFeedWrapper>
          {shouldShowRightSidebar && <RightSidebar />}
        </VersionedSpaceWrapper>
      </StandardLayout.Body>
    </VersionedContent>
  );
};

export default EventsSpace;
